import { useCallback } from 'react';
import { DocumentSkelton } from 'api/document/types/document';
import { SpaceTabSkelton } from 'api/space';
import { Task } from 'api/tasks';
import { Suggestion, MentionType } from 'vendor/ckeditor/plugins/mention/types';
import { setActiveSuggestion, useMentions } from '../mention-store';

export const useSelectSuggestion = (activeTab) => {
  const [contextState, contextDispatch] = useMentions();

  const handler = useCallback(
    ({
      direction,
      data,
      type,
      name,
      id,
    }: {
      direction: -1 | 1;
      data: DocumentSkelton[] | Task[] | SpaceTabSkelton[];
      type: MentionType;
      name: string;
      id: string;
    }) => {
      const getSuggestionItem = (item) => {
        return {
          name: item[name],
          type,
          subType: item.type,
          objectId: item[id],
        };
      };

      if (activeTab !== type) {
        // Don't handle suggestion if not in active tab
        return;
      }
      const { activeSuggestion } = contextState;
      // Select first/last suggestion based on the direction
      const fallbackOffset = direction > 0 ? 0 : data.length - 1;

      // Select first item if no suggestion is selected if dir is down and vice versa
      if (!activeSuggestion || activeSuggestion.type !== type) {
        if (data[fallbackOffset]) {
          const suggestionItem: Suggestion = getSuggestionItem(
            data[fallbackOffset],
          );

          if ((data[fallbackOffset] as SpaceTabSkelton).space) {
            suggestionItem.spaceId = (
              data[fallbackOffset] as SpaceTabSkelton
            ).space.spaceId;
          }

          if ((data[fallbackOffset] as SpaceTabSkelton).project != null) {
            suggestionItem.projectId = (
              data[fallbackOffset] as SpaceTabSkelton
            ).project?.projectId;
          }

          contextDispatch(setActiveSuggestion(suggestionItem));
        }
      } else if (activeSuggestion?.type === type) {
        // Select next item if present
        const currentActiveSuggPosition = data.findIndex(
          (item) => item[id] === activeSuggestion?.objectId,
        );

        if (data[currentActiveSuggPosition + direction]) {
          const suggestionItem: Suggestion = getSuggestionItem(
            data[currentActiveSuggPosition + direction],
          );

          if ((data[currentActiveSuggPosition] as SpaceTabSkelton).space) {
            suggestionItem.spaceId = (
              data[currentActiveSuggPosition] as SpaceTabSkelton
            ).space.spaceId;
          }

          if ((data[fallbackOffset] as SpaceTabSkelton).project != null) {
            suggestionItem.projectId = (
              data[fallbackOffset] as SpaceTabSkelton
            ).project?.projectId;
          }

          contextDispatch(setActiveSuggestion(suggestionItem));
        }
      }
    },
    [activeTab, contextDispatch, contextState],
  );

  return handler;
};

export const useSelectFirstSuggestion = (activeTab) => {
  const [contextState, contextDispatch] = useMentions();

  const handler = useCallback(
    ({ data, type, name, id }) => {
      const { activeSuggestion } = contextState;

      if (
        !data[0] ||
        activeTab !== type ||
        activeSuggestion?.objectId === data[0][id]
      ) {
        // Don't handle suggestion if not in active tab
        return;
      }
      const suggestionItem = {
        name: data[0][name],
        type,
        subType: data[0].type,
        spaceId: data[0]?.space?.spaceId,
        ...(data[0].project != null
          ? { projectId: data[0]?.project?.projectId }
          : {}),
        objectId: data[0][id],
      };

      contextDispatch(setActiveSuggestion(suggestionItem));
    },
    [activeTab, contextDispatch, contextState],
  );

  return handler;
};
